import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Upload, GripVertical, Images } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${BACKEND_URL}${url}`);

const CATEGORY_OPTIONS = [
  { value: 'lips', label: 'Lips' },
  { value: 'wrinkles', label: 'Anti Wrinkles' },
  { value: 'fillers', label: 'Fillers' },
  { value: 'jawline', label: 'Jawline' },
  { value: 'teartroughs', label: 'Tear Troughs' },
  { value: 'facerejuvenation', label: 'Face Rejuvenation' },
  { value: 'chinfiller', label: 'Chin Filler' }
];

const ICON_OPTIONS = ['Sparkles', 'Syringe', 'Heart', 'Star', 'User', 'Smile'];

const categoryLabel = (v) => (CATEGORY_OPTIONS.find((c) => c.value === v)?.label) || v;

// Shared delete confirm hook
const useDelete = (onConfirm) => {
  const [target, setTarget] = useState(null);
  const confirm = async () => {
    if (target) await onConfirm(target);
    setTarget(null);
  };
  return { target, setTarget, confirm };
};

// Reusable native drag-and-drop reorder hook
const useDragReorder = (items, setItems, kind, API, authConfig, reload) => {
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const persist = async (ordered) => {
    try {
      await axios.post(`${API}/content/reorder`, { kind, ordered_ids: ordered.map((x) => x.id) }, authConfig);
      toast.success('Order saved');
    } catch {
      toast.error('Could not save order');
      reload();
    }
  };

  const onDragStart = (i) => () => { dragIndex.current = i; };
  const onDragOver = (i) => (e) => { e.preventDefault(); setOverIndex(i); };
  const onDragEnd = () => { dragIndex.current = null; setOverIndex(null); };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    setOverIndex(null);
    if (from === null || from === i) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setItems(next);
    dragIndex.current = null;
    persist(next);
  };

  return { overIndex, onDragStart, onDragOver, onDragEnd, onDrop };
};

// ==================== GALLERY ====================
export const GalleryManager = ({ API, authConfig }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: 'lips', title: '', image: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const del = useDelete(async (id) => {
    try {
      await axios.delete(`${API}/content/gallery/${id}`, authConfig);
      toast.success('Image deleted');
      load();
    } catch { toast.error('Delete failed'); }
  });

  const load = () => axios.get(`${API}/content/gallery`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const drag = useDragReorder(items, setItems, 'gallery', API, authConfig, load);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('lips');
  const [bulkUploading, setBulkUploading] = useState(false);

  const onBulkChange = (e) => setBulkFiles(Array.from(e.target.files || []));
  const bulkSave = async () => {
    if (!bulkFiles.length) { toast.error('Please choose at least one photo'); return; }
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('category', bulkCategory);
      bulkFiles.forEach((f) => fd.append('files', f));
      const res = await axios.post(`${API}/content/gallery/bulk`, fd, {
        headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.length} photo(s) added`);
      setBulkOpen(false); setBulkFiles([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ category: 'lips', title: '', image: '' });
    setFile(null); setPreview('');
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ category: item.category, title: item.title, image: item.image });
    setFile(null); setPreview('');
    setDialogOpen(true);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!editing && !file) { toast.error('Please choose an image'); return; }
    setSaving(true);
    try {
      let imageUrl = form.image;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await axios.post(`${API}/content/upload`, fd, {
          headers: { ...authConfig.headers, 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = up.data.image;
      }
      const payload = { category: form.category, title: form.title, image: imageUrl };
      if (editing) {
        await axios.put(`${API}/content/gallery/${editing.id}`, payload, authConfig);
        toast.success('Image updated');
      } else {
        await axios.post(`${API}/content/gallery`, payload, authConfig);
        toast.success('Image added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <p className="text-gray-600">{items.length} gallery image(s)</p>
        <div className="flex gap-2">
          <Button data-testid="gallery-bulk-btn" onClick={() => { setBulkFiles([]); setBulkCategory('lips'); setBulkOpen(true); }} variant="outline" className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white">
            <Images className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button data-testid="gallery-add-btn" onClick={openAdd} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Image
          </Button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4">Tip: drag any photo to reorder how it appears on the website.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item, idx) => (
          <Card
            key={item.id}
            data-testid={`admin-gallery-${item.id}`}
            draggable
            onDragStart={drag.onDragStart(idx)}
            onDragOver={drag.onDragOver(idx)}
            onDragEnd={drag.onDragEnd}
            onDrop={drag.onDrop(idx)}
            className={`overflow-hidden border transition-all ${drag.overIndex === idx ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]' : 'border-gray-200'}`}>
            <div className="relative">
              <img src={resolveImg(item.image)} alt={item.title} className="w-full h-40 object-cover" />
              <span className="absolute top-2 left-2 flex items-center justify-center h-7 w-7 rounded bg-black/50 text-white cursor-grab active:cursor-grabbing" title="Drag to reorder">
                <GripVertical className="w-4 h-4" />
              </span>
            </div>
            <CardContent className="p-3">
              <p className="font-semibold text-sm text-black truncate">{item.title}</p>
              <Badge variant="outline" className="mt-1 border-[#D4AF37] text-[#D4AF37] text-xs">{categoryLabel(item.category)}</Badge>
              <div className="flex gap-2 mt-3">
                <Button data-testid={`gallery-edit-${item.id}`} size="sm" variant="outline" className="flex-1 border-gray-300" onClick={() => openEdit(item)}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button data-testid={`gallery-delete-${item.id}`} size="sm" variant="outline" className="flex-1 border-red-300 text-red-500 hover:bg-red-500 hover:text-white" onClick={() => del.setTarget(item.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Image' : 'Add Image'}</DialogTitle>
            <DialogDescription>Upload a photo and set its title and category.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Photo {editing && <span className="text-xs text-gray-500">(leave empty to keep current)</span>}</Label>
              <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-[#D4AF37] rounded-lg p-4 cursor-pointer hover:bg-[#D4AF37]/5">
                {(preview || (editing && form.image)) ? (
                  <img src={preview || resolveImg(form.image)} alt="preview" className="h-32 object-contain" />
                ) : (
                  <span className="flex flex-col items-center text-gray-500 text-sm"><Upload className="w-6 h-6 mb-1" /> Click to choose a photo</span>
                )}
                <input data-testid="gallery-file-input" type="file" accept="image/*" className="hidden" onChange={onFileChange} />
              </label>
            </div>
            <div>
              <Label>Title</Label>
              <Input data-testid="gallery-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Lip Filler Results" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="gallery-save-btn" disabled={saving} onClick={save} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Upload Photos</DialogTitle>
            <DialogDescription>Choose several photos at once — they'll all be added to the selected category.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger data-testid="bulk-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Photos</Label>
              <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-[#D4AF37] rounded-lg p-4 cursor-pointer hover:bg-[#D4AF37]/5">
                <span className="flex flex-col items-center text-gray-500 text-sm">
                  <Images className="w-6 h-6 mb-1" />
                  {bulkFiles.length ? `${bulkFiles.length} photo(s) selected` : 'Click to choose multiple photos'}
                </span>
                <input data-testid="bulk-file-input" type="file" accept="image/*" multiple className="hidden" onChange={onBulkChange} />
              </label>
              {bulkFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {bulkFiles.slice(0, 8).map((f, i) => (
                    <img key={i} src={URL.createObjectURL(f)} alt={f.name} className="h-14 w-full object-cover rounded" />
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button data-testid="bulk-save-btn" disabled={bulkUploading} onClick={bulkSave} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">
              {bulkUploading ? 'Uploading...' : `Upload ${bulkFiles.length || ''}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm del={del} label="image" />
    </div>
  );
};

// ==================== SERVICES ====================
export const ServicesManager = ({ API, authConfig }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', brands: '', icon: 'Sparkles' });
  const [saving, setSaving] = useState(false);
  const del = useDelete(async (id) => {
    try {
      await axios.delete(`${API}/content/services/${id}`, authConfig);
      toast.success('Service deleted');
      load();
    } catch { toast.error('Delete failed'); }
  });

  const load = () => axios.get(`${API}/content/services`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const drag = useDragReorder(items, setItems, 'services', API, authConfig, load);

  const openAdd = () => {
    setEditing(null);
    setForm({ title: '', description: '', brands: '', icon: 'Sparkles' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, description: item.description, brands: (item.brands || []).join(', '), icon: item.icon });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        brands: form.brands.split(',').map((b) => b.trim()).filter(Boolean),
        icon: form.icon
      };
      if (editing) {
        await axios.put(`${API}/content/services/${editing.id}`, payload, authConfig);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/content/services`, payload, authConfig);
        toast.success('Service added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <p className="text-gray-600">{items.length} service(s)</p>
        <Button data-testid="service-add-btn" onClick={openAdd} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>
      <p className="text-xs text-gray-400 mb-4">Tip: drag a service to reorder it in the website carousel.</p>

      <div className="space-y-3">
        {items.map((item, idx) => (
          <Card
            key={item.id}
            data-testid={`admin-service-${item.id}`}
            draggable
            onDragStart={drag.onDragStart(idx)}
            onDragOver={drag.onDragOver(idx)}
            onDragEnd={drag.onDragEnd}
            onDrop={drag.onDrop(idx)}
            className={`transition-all ${drag.overIndex === idx ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]' : 'border border-gray-200'}`}>
            <CardContent className="p-4 flex justify-between items-start gap-4">
              <span className="mt-1 text-gray-300 cursor-grab active:cursor-grabbing" title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
              <div className="flex-1">
                <p className="font-semibold text-black">{item.title} <span className="text-xs text-gray-400">({item.icon})</span></p>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(item.brands || []).map((b, i) => <Badge key={i} variant="outline" className="border-[#D4AF37] text-[#D4AF37] text-xs">{b}</Badge>)}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button data-testid={`service-edit-${item.id}`} size="sm" variant="outline" className="border-gray-300" onClick={() => openEdit(item)}><Pencil className="w-3 h-3" /></Button>
                <Button data-testid={`service-delete-${item.id}`} size="sm" variant="outline" className="border-red-300 text-red-500 hover:bg-red-500 hover:text-white" onClick={() => del.setTarget(item.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Service' : 'Add Service'}</DialogTitle>
            <DialogDescription>Set the treatment name, description, brands and icon.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="service-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="service-desc-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Brands <span className="text-xs text-gray-500">(comma separated)</span></Label>
              <Input data-testid="service-brands-input" value={form.brands} onChange={(e) => setForm({ ...form, brands: e.target.value })} placeholder="Botox, Juvederm" />
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="service-save-btn" disabled={saving} onClick={save} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm del={del} label="service" />
    </div>
  );
};

// ==================== PRICING ====================
export const PricingManager = ({ API, authConfig }) => {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ treatment: '', price: '' });
  const [saving, setSaving] = useState(false);
  const del = useDelete(async (id) => {
    try {
      await axios.delete(`${API}/content/pricing/${id}`, authConfig);
      toast.success('Price removed');
      load();
    } catch { toast.error('Delete failed'); }
  });

  const load = () => axios.get(`${API}/content/pricing`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const drag = useDragReorder(items, setItems, 'pricing', API, authConfig, load);

  const openAdd = () => { setEditing(null); setForm({ treatment: '', price: '' }); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ treatment: item.treatment, price: item.price }); setDialogOpen(true); };

  const save = async () => {
    if (!form.treatment.trim() || !form.price.trim()) { toast.error('Treatment and price are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/content/pricing/${editing.id}`, form, authConfig);
        toast.success('Price updated');
      } else {
        await axios.post(`${API}/content/pricing`, form, authConfig);
        toast.success('Price added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <p className="text-gray-600">{items.length} price item(s)</p>
        <Button data-testid="price-add-btn" onClick={openAdd} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Price
        </Button>
      </div>
      <p className="text-xs text-gray-400 mb-4">Tip: drag a row to reorder the price list on the website.</p>

      <div className="space-y-2">
        {items.map((item, idx) => (
          <Card
            key={item.id}
            data-testid={`admin-price-${item.id}`}
            draggable
            onDragStart={drag.onDragStart(idx)}
            onDragOver={drag.onDragOver(idx)}
            onDragEnd={drag.onDragEnd}
            onDrop={drag.onDrop(idx)}
            className={`transition-all ${drag.overIndex === idx ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]' : 'border border-gray-200'}`}>
            <CardContent className="p-3 flex justify-between items-center gap-4">
              <span className="text-gray-300 cursor-grab active:cursor-grabbing" title="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
              <div className="flex-1 flex justify-between items-center pr-4">
                <span className="font-medium text-black">{item.treatment}</span>
                <span className="font-bold text-[#D4AF37]">{item.price}</span>
              </div>
              <div className="flex gap-2">
                <Button data-testid={`price-edit-${item.id}`} size="sm" variant="outline" className="border-gray-300" onClick={() => openEdit(item)}><Pencil className="w-3 h-3" /></Button>
                <Button data-testid={`price-delete-${item.id}`} size="sm" variant="outline" className="border-red-300 text-red-500 hover:bg-red-500 hover:text-white" onClick={() => del.setTarget(item.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Price' : 'Add Price'}</DialogTitle>
            <DialogDescription>Set the treatment name and its price.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Treatment</Label>
              <Input data-testid="price-treatment-input" value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} placeholder="e.g. Lip Filler - 1ml" />
            </div>
            <div>
              <Label>Price</Label>
              <Input data-testid="price-value-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. £180" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="price-save-btn" disabled={saving} onClick={save} className="bg-[#D4AF37] hover:bg-[#B8941F] text-white">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm del={del} label="price" />
    </div>
  );
};

// Shared delete confirmation dialog
const DeleteConfirm = ({ del, label }) => (
  <AlertDialog open={!!del.target} onOpenChange={(o) => !o && del.setTarget(null)}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="confirm-delete-btn" onClick={del.confirm} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
